import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { cn, formatDate, formatDuration, getStatusColor } from '@/lib/utils';
import { apiService as mockApi } from '@/api';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal, ConfirmDialog } from '@/components/ui/Modal';
import { toast } from '@/components/ui/Toast';
import type { Device, RemoteSession } from '@/types';
import {
  Monitor,
  Play,
  RotateCw,
  Power,
  Shield,
  Clock,
  Wifi,
  Copy,
  Camera,
  Maximize2,
  Minimize2,
  Lock,
  Unlock,
  AlertTriangle,
  CheckCircle,
  FileText,
  Activity,
  ArrowLeft,
  X,
  Sliders,
  Maximize,
  Keyboard,
  MousePointer,
  HelpCircle,
  Eye,
  Settings,
  MessageSquare,
} from 'lucide-react';

interface ActivityLogItem {
  id: string;
  timestamp: string;
  type: 'connect' | 'mode_change' | 'ctrl_alt_del' | 'screenshot' | 'clipboard' | 'monitor_change';
  description: string;
}

export function RemoteDesktop() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [device, setDevice] = useState<Device | null>(null);
  const [loadingDevice, setLoadingDevice] = useState(true);

  // Session Connection State
  const [sessionState, setSessionSecondsState] = useState<'pre_connect' | 'connecting' | 'connected' | 'disconnected'>('pre_connect');
  const [connectionStep, setConnectionStep] = useState('Waiting for agent screenshot...');
  const [screenshotUrl, setScreenshotUrl] = useState<string>('');

  // Configs before connect
  const [sessionMode, setSessionMode] = useState<'control' | 'view_only'>('control');
  const [qualityPreset, setQualityPreset] = useState<'high' | 'balanced' | 'low'>('high');
  const [userConsentGiven, setUserConsentGiven] = useState(true);

  // Live session controls
  const [selectedMonitor, setSelectedMonitor] = useState<'monitor_1' | 'monitor_2' | 'all'>('monitor_1');
  const [scaleOption, setScaleOption] = useState<'fit' | 'actual' | 'stretch'>('fit');
  const [clipboardSync, setClipboardSync] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSidePanel, setShowSidePanel] = useState(true);

  // Session stats & timers
  const [sessionDuration, setSessionDuration] = useState(0);
  const [idleTimeout, setIdleTimeout] = useState(900); // 15 min countdown
  const [latencyMs, setLatencyMs] = useState(42);
  const [latencyStatus, setLatencyStatus] = useState<'good' | 'fair' | 'poor'>('good' as any);

  // Technician notes & action logs
  const [sessionNotes, setSessionNotes] = useState('Client reported slow app launch. Checking Task Manager.');
  const [activityLogs, setActivityLogs] = useState<ActivityLogItem[]>([]);

  // Modals
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  // Live Desktop simulation state
  const [mockClockTime, setMockClockTime] = useState(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
  const [mousePos, setMousePos] = useState({ x: 450, y: 280 });

  // Fetch device details
  useEffect(() => {
    let isMounted = true;
    async function loadData() {
      setLoadingDevice(true);
      try {
        if (id) {
          const d = await mockApi.getDevice(id);
          if (isMounted) {
            setDevice(d);
          }
        } else {
          const res = await mockApi.getDevices({ pageSize: 1 });
          if (isMounted && res.data.length > 0) setDevice(res.data[0]);
        }
      } catch (error) {
        console.error('Error loading device data:', error);
      } finally {
        if (isMounted) {
          setLoadingDevice(false);
        }
      }
    }
    loadData();
    return () => { isMounted = false; };
  }, [id]);

  // Handle active session timers & latency jitter simulation
  useEffect(() => {
    if (sessionState !== 'connected') return;

    const timer = setInterval(() => {
      setSessionDuration((prev) => prev + 1);
      setIdleTimeout((prev) => (prev > 0 ? prev - 1 : 0));

      // Simulate live network jitter (42ms ± 15ms)
      const jitter = Math.floor(Math.random() * 20) - 8;
      const nextLatency = Math.max(18, Math.min(240, 42 + jitter));
      setLatencyMs(nextLatency);

      // Desktop taskbar clock update
      setMockClockTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    }, 1000);

    return () => clearInterval(timer);
  }, [sessionState]);

  // Start Remote Session Handshake Sequence
  const grabScreenshot = useCallback(async (): Promise<boolean> => {
    if (!id) return false;
    const api = mockApi as any;
    if (typeof api.captureScreenshot !== 'function') {
      throw new Error('Screenshot API is not available');
    }
    const res = await api.captureScreenshot(id);
    const image = res?.image || res?.data?.image;
    if (typeof image === 'string' && image.startsWith('data:image')) {
      setScreenshotUrl(image);
      return true;
    }
    throw new Error(res?.message || 'Agent did not return a desktop image');
  }, [id]);

  useEffect(() => {
    if (sessionState !== 'connected') return;
    let cancelled = false;
    const loop = async () => {
      while (!cancelled) {
        try {
          await grabScreenshot();
        } catch {
          // keep last frame
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
    };
    void loop();
    return () => {
      cancelled = true;
    };
  }, [sessionState, grabScreenshot]);

  const handleStartSession = async () => {
    setSessionSecondsState('connecting');
    setConnectionStep('Sending screenshot command to the agent...');
    try {
      const ok = await grabScreenshot();
      if (!ok) {
        throw new Error('No screenshot returned');
      }
      setSessionSecondsState('connected');
      logActivity('connect', `Live desktop stream started (${sessionMode.toUpperCase()}).`);
      toast('Remote Desktop connected — live screenshots from the agent', 'success');
    } catch (err: any) {
      setSessionSecondsState('pre_connect');
      toast(err?.message || 'Could not connect. Restart InsightNodeAgent.exe and try again.', 'error');
    }
  };

  // Log activity helper
  const logActivity = (type: ActivityLogItem['type'], description: string) => {
    const item: ActivityLogItem = {
      id: Math.random().toString(),
      timestamp: new Date().toLocaleTimeString(),
      type,
      description,
    };
    setActivityLogs((prev) => [item, ...prev]);
  };

  // Actions
  const handleSendCtrlAltDel = () => {
    logActivity('ctrl_alt_del', 'Sent Ctrl+Alt+Del secure key combination.');
    toast('Ctrl+Alt+Del signal sent to remote endpoint', 'info');
  };

  const handleCaptureScreenshot = async () => {
    try {
      await grabScreenshot();
      logActivity('screenshot', 'Captured remote desktop viewport screenshot.');
      toast('Desktop frame refreshed', 'success');
    } catch (err: any) {
      toast(err?.message || 'Screenshot failed', 'error');
    }
  };

  const handleToggleMode = () => {
    const nextMode = sessionMode === 'control' ? 'view_only' : 'control';
    setSessionMode(nextMode);
    logActivity('mode_change', `Switched session mode to ${nextMode.toUpperCase()}.`);
    toast(`Session mode changed to ${nextMode === 'control' ? 'Full Control' : 'View Only'}`, 'info');
  };

  const handleConfirmDisconnect = () => {
    setShowDisconnectModal(false);
    setSessionSecondsState('disconnected');
    toast('Remote session disconnected', 'info');
    navigate('/remote-sessions');
  };

  const handleMouseMoveViewport = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.round(e.clientX - rect.left);
    const y = Math.round(e.clientY - rect.top);
    setMousePos({ x, y });
    // Reset idle timeout on activity
    if (idleTimeout < 800) setIdleTimeout(900);
  };

  return (
    <div className={cn('flex flex-col h-[calc(100vh-3.5rem)] bg-bg text-text-primary overflow-hidden', isFullscreen && 'fixed inset-0 z-50 h-screen w-screen')}>
      {/* Pre-Connection Setup Screen */}
      {sessionState === 'pre_connect' && (
        <div className="flex-1 flex items-center justify-center bg-bg p-6">
          <Card className="max-w-xl w-full surface-elevated border-border p-6 space-y-6 animate-slide-up">
            <div className="flex items-center justify-between border-b border-border pb-4">
              <div className="flex items-center gap-3">
                <Monitor className="h-6 w-6 text-accent" />
                <div>
                  <h2 className="text-base font-semibold text-text-primary">Remote Desktop Session</h2>
                  <p className="text-2xs text-text-muted">Target Device: {device?.hostname || 'ENDPOINT-01'}</p>
                </div>
              </div>
              <Badge variant="online">Agent Ready</Badge>
            </div>

            {/* Target device quick stats */}
            <div className="grid grid-cols-2 gap-3 text-xs bg-panel p-3 rounded-lg border border-border font-mono">
              <div>
                <span className="text-text-muted block text-2xs">OS Version</span>
                <span className="text-text-primary font-semibold">{device?.os || 'Windows 11 Pro'}</span>
              </div>
              <div>
                <span className="text-text-muted block text-2xs">IP Address</span>
                <span className="text-text-primary font-semibold">{device?.localIp || '192.168.1.105'}</span>
              </div>
              <div>
                <span className="text-text-muted block text-2xs">Logged-in User</span>
                <span className="text-text-primary font-semibold">{device?.loggedInUser || 'DOMAIN\\admin'}</span>
              </div>
              <div>
                <span className="text-text-muted block text-2xs">Agent Version</span>
                <span className="text-text-primary font-semibold">{device?.agentVersion || 'v2.4.12'}</span>
              </div>
            </div>

            {/* Mode selection */}
            <div className="space-y-3">
              <label className="text-xs font-semibold text-text-secondary block">Select Interaction Mode:</label>
              <div className="grid grid-cols-2 gap-3">
                <div
                  onClick={() => setSessionMode('control')}
                  className={cn(
                    'p-3 rounded-lg border cursor-pointer flex flex-col gap-1 transition-all',
                    sessionMode === 'control'
                      ? 'border-accent bg-accent/10'
                      : 'border-border bg-panel hover:bg-hover'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-text-primary">Control Mode</span>
                    <Unlock className="h-3.5 w-3.5 text-accent" />
                  </div>
                  <span className="text-2xs text-text-muted">Full keyboard, mouse, and clipboard interaction</span>
                </div>

                <div
                  onClick={() => setSessionMode('view_only')}
                  className={cn(
                    'p-3 rounded-lg border cursor-pointer flex flex-col gap-1 transition-all',
                    sessionMode === 'view_only'
                      ? 'border-accent bg-accent/10'
                      : 'border-border bg-panel hover:bg-hover'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-text-primary">View Only Mode</span>
                    <Eye className="h-3.5 w-3.5 text-amber-400" />
                  </div>
                  <span className="text-2xs text-text-muted">Monitor remote screen without input passthrough</span>
                </div>
              </div>
            </div>

            {/* Quality presets */}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-text-secondary block">Performance & Quality Preset:</label>
              <div className="flex items-center gap-2">
                <Button
                  variant={qualityPreset === 'high' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setQualityPreset('high')}
                  className="flex-1 text-2xs"
                >
                  High Quality (1080p, 60fps)
                </Button>
                <Button
                  variant={qualityPreset === 'balanced' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setQualityPreset('balanced')}
                  className="flex-1 text-2xs"
                >
                  Balanced (720p, 30fps)
                </Button>
                <Button
                  variant={qualityPreset === 'low' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setQualityPreset('low')}
                  className="flex-1 text-2xs"
                >
                  Low Bandwidth
                </Button>
              </div>
            </div>

            {/* User Consent Toggle */}
            <div className="flex items-center justify-between p-3 rounded bg-panel border border-border text-xs">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-emerald-400" />
                <div>
                  <span className="font-semibold block">Request End-User Consent</span>
                  <span className="text-2xs text-text-muted">Show consent prompt on user screen before desktop attachment</span>
                </div>
              </div>
              <input
                type="checkbox"
                checked={userConsentGiven}
                onChange={(e) => setUserConsentGiven(e.target.checked)}
                className="rounded border-border bg-bg text-accent focus:ring-0"
              />
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-border pt-4">
              <Button variant="ghost" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleStartSession} className="px-6">
                <Play className="h-4 w-4 mr-1.5" /> Start Session
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Connecting Loading Screen */}
      {sessionState === 'connecting' && (
        <div className="flex-1 flex flex-col items-center justify-center bg-bg p-6 text-center">
          <RotateCw className="h-10 w-10 text-accent animate-spin mb-4" />
          <h3 className="text-base font-semibold mb-2">Establishing Remote Session</h3>
          <p className="text-xs text-text-muted font-mono mb-6 max-w-sm">{connectionStep}</p>

          <div className="w-64 bg-border h-1.5 rounded-full overflow-hidden">
            <div className="bg-accent h-full animate-pulse w-3/4" />
          </div>
        </div>
      )}

      {/* Active Session Screen */}
      {sessionState === 'connected' && (
        <>
          {/* Endpoint Notification & Consent Banner */}
          <div className="bg-panel border-b border-border px-4 py-1.5 flex flex-wrap items-center justify-between text-xs shrink-0">
            <div className="flex items-center gap-2 text-text-primary">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0" />
              <span>A remote desktop session is active on this device. The endpoint user has been notified.</span>
            </div>

            <div className="flex items-center gap-3 font-mono text-2xs">
              {userConsentGiven ? (
                <span className="text-emerald-400 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" /> Consent granted by user
                </span>
              ) : (
                <span className="text-amber-400 flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Bypassed (Admin Override)
                </span>
              )}
            </div>
          </div>

          {/* Workspace Top Toolbar */}
          <div className="flex flex-wrap items-center justify-between px-4 py-2 bg-panel border-b border-border gap-2 shrink-0">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => setShowDisconnectModal(true)} className="text-text-muted hover:text-text-primary">
                <ArrowLeft className="h-4 w-4" />
              </Button>

              <div className="flex items-center gap-2 font-mono text-xs">
                <Monitor className="h-4 w-4 text-accent" />
                <span className="font-semibold text-text-primary">{device?.hostname || 'ENDPOINT-01'}</span>
                <Badge variant={sessionMode === 'control' ? 'online' : 'warning'} className="uppercase">
                  {sessionMode}
                </Badge>
              </div>

              <div className="h-4 w-px bg-border my-auto mx-1" />

              {/* Quality & Latency Indicator */}
              <div className="flex items-center gap-2 text-2xs font-mono text-text-muted">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                <span className="text-text-primary">{latencyMs}ms</span>
                <span>({qualityPreset.toUpperCase()})</span>
              </div>

              <div className="h-4 w-px bg-border my-auto mx-1" />

              {/* Timers */}
              <div className="flex items-center gap-3 text-2xs font-mono text-text-muted">
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span>Session: {formatDuration(sessionDuration)}</span>
                </div>
                <span>|</span>
                <span>Idle timeout: {Math.floor(idleTimeout / 60)}m {idleTimeout % 60}s</span>
              </div>
            </div>

            {/* Right Action Controls */}
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={handleToggleMode} title="Toggle Control/View Mode">
                {sessionMode === 'control' ? <Unlock className="h-3.5 w-3.5 text-accent" /> : <Eye className="h-3.5 w-3.5 text-amber-400" />}
                <span className="hidden sm:inline text-xs">{sessionMode === 'control' ? 'Control' : 'View Only'}</span>
              </Button>

              {/* Monitor Selector */}
              <select
                value={selectedMonitor}
                onChange={(e) => {
                  setSelectedMonitor(e.target.value as any);
                  logActivity('monitor_change', `Switched display viewport to ${e.target.value}.`);
                }}
                className="bg-elevated border border-border rounded px-2 py-1 text-xs text-text-primary focus:outline-none font-mono cursor-pointer"
              >
                <option value="monitor_1">Display 1 (1920x1080)</option>
                <option value="monitor_2">Display 2 (1920x1080)</option>
                <option value="all">Span All Displays</option>
              </select>

              {/* Scale Options */}
              <select
                value={scaleOption}
                onChange={(e) => setScaleOption(e.target.value as any)}
                className="bg-elevated border border-border rounded px-2 py-1 text-xs text-text-primary focus:outline-none font-mono cursor-pointer"
              >
                <option value="fit">Fit to Viewport</option>
                <option value="actual">Actual Size (100%)</option>
                <option value="stretch">Stretch</option>
              </select>

              <Button variant="ghost" size="sm" onClick={handleSendCtrlAltDel} title="Send Ctrl+Alt+Del">
                <Keyboard className="h-3.5 w-3.5 text-text-muted" />
                <span className="hidden sm:inline text-xs">Ctrl+Alt+Del</span>
              </Button>

              <Button variant="ghost" size="sm" onClick={handleCaptureScreenshot} title="Take Screenshot">
                <Camera className="h-3.5 w-3.5 text-text-muted" />
              </Button>

              <Button
                variant={showSidePanel ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setShowSidePanel(!showSidePanel)}
                title="Toggle Side Panel"
              >
                <FileText className="h-3.5 w-3.5" />
              </Button>

              <Button variant="ghost" size="sm" onClick={() => setIsFullscreen(!isFullscreen)} title="Fullscreen">
                {isFullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              </Button>

              <Button variant="danger" size="sm" onClick={() => setShowDisconnectModal(true)}>
                <Power className="h-3.5 w-3.5" /> Disconnect
              </Button>
            </div>
          </div>

          {/* Main Remote Viewport Area & Collapsible Side Panel */}
          <div className="flex-1 flex overflow-hidden relative">
            {/* Virtual Remote Desktop Viewport */}
            <div
              className="flex-1 bg-[#050507] p-4 flex items-center justify-center overflow-auto relative select-none"
              onMouseMove={handleMouseMoveViewport}
            >
              {screenshotUrl ? (
                <img
                  src={screenshotUrl}
                  alt="Remote desktop"
                  className={cn(
                    'max-w-full max-h-full rounded-lg border border-border shadow-2xl',
                    scaleOption === 'fit' && 'object-contain',
                    scaleOption === 'actual' && 'object-none',
                    scaleOption === 'stretch' && 'w-full h-full object-fill'
                  )}
                />
              ) : (
                <div className="text-xs text-text-muted font-mono">Waiting for desktop frame from agent...</div>
              )}
            </div>

            {/* Collapsible Technician Side Panel */}
            {showSidePanel && (
              <div className="w-80 bg-panel border-l border-border flex flex-col h-full shrink-0 animate-slide-up">
                {/* Side Panel Header */}
                <div className="p-3 border-b border-border flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs font-semibold">
                    <FileText className="h-4 w-4 text-accent" />
                    <span>Session Notes & Audit</span>
                  </div>
                  <button onClick={() => setShowSidePanel(false)} className="text-text-muted hover:text-text-primary">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>

                {/* Session Notes Editor */}
                <div className="p-3 border-b border-border space-y-2">
                  <label className="text-2xs font-semibold text-text-muted uppercase tracking-wider block">
                    Technician Notes
                  </label>
                  <textarea
                    value={sessionNotes}
                    onChange={(e) => setSessionNotes(e.target.value)}
                    placeholder="Enter notes about actions performed during session..."
                    className="w-full h-24 bg-bg border border-border rounded p-2 text-xs text-text-primary focus:outline-none focus:border-accent resize-none font-sans"
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-2xs text-text-muted">Auto-saves to audit trail</span>
                    <Button variant="ghost" size="sm" onClick={() => toast('Session notes updated', 'success')} className="text-2xs">
                      Save
                    </Button>
                  </div>
                </div>

                {/* Session Metadata Info */}
                <div className="p-3 border-b border-border space-y-1.5 text-2xs font-mono text-text-secondary">
                  <div className="flex justify-between">
                    <span>Session ID:</span>
                    <span className="text-text-primary font-semibold">RDS-2026-8821</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Technician:</span>
                    <span className="text-text-primary font-semibold">admin (System Admin)</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Start Time:</span>
                    <span className="text-text-primary">{formatDate(new Date().toISOString())}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Target IP:</span>
                    <span className="text-text-primary">{device?.publicIp || '198.51.100.42'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Protocol:</span>
                    <span className="text-text-primary">WebRTC / TLS 1.3</span>
                  </div>
                </div>

                {/* Activity Stream */}
                <div className="flex-1 p-3 overflow-y-auto space-y-2">
                  <span className="text-2xs font-semibold text-text-muted uppercase tracking-wider block mb-2">
                    Session Activity Log
                  </span>

                  {activityLogs.map((log) => (
                    <div key={log.id} className="surface-elevated p-2 rounded border border-border/60 text-2xs space-y-1">
                      <div className="flex items-center justify-between font-mono text-text-muted">
                        <span className="font-semibold text-accent capitalize">{log.type}</span>
                        <span>{log.timestamp}</span>
                      </div>
                      <p className="text-text-secondary leading-snug">{log.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Disconnect Confirmation Modal */}
      <ConfirmDialog
        open={showDisconnectModal}
        onClose={() => setShowDisconnectModal(false)}
        onConfirm={handleConfirmDisconnect}
        title="Disconnect Remote Desktop Session"
        confirmLabel="Disconnect Session"
        danger
        message={
          <div className="space-y-2 text-xs">
            <p>
              Are you sure you want to end the active remote session on <strong>{device?.hostname || 'ENDPOINT'}</strong>?
            </p>
            <p className="text-2xs text-text-muted">
              Session duration and notes will be saved to the central audit log.
            </p>
          </div>
        }
      />
    </div>
  );
}
